import { type ReactElement, useCallback, useEffect, useState } from 'react';
import type { ApiError, FoodMerchantLocationView, FoodMerchantView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { Badge, Button, Cluster, Drawer, Field, MoneyInput, Notice, Select, TextInput, DrawerHeader, DrawerBody, Stack, Text } from '@vello/web-theme';
import {
  HOURS_DAYS, emptyWeek, hasOpenHours, hasStoreResponsible, mayManageStoreTeam, parseCoordinate, sameEveryDay, setDay, storeLiveProgress, storeOnboardingActions, weekFromWindows, weekToWindows,
  type DayHours, type HoursDay, type StoreHoursIssue, type StoreLiveFacts, type StoreLiveStep, type WeekHours,
} from '../../src/index';
import { useAdmin } from '../session';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { DrawerStack } from './drawer-stack';
import { ScopeMarketNotice, useScopeMarket } from './scope-market';
import { StoreResponsibleInvite } from './store-responsible-invite';
const newId = (): string => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `id-${Date.now()}-${Math.random().toString(16).slice(2)}`);

export function StoreLiveDrawer({ open, merchant, onClose, onChanged }: { open: boolean; merchant: FoodMerchantView | undefined; onClose: () => void; onChanged: () => void }): ReactElement {
  const { runtime, grants, markets } = useAdmin();
  const { market: scopeMarket, choices: marketChoices } = useScopeMarket();
  const { t, lang } = useT();
  const { v, fail, explain: said } = useVocab();
  const allowed = storeOnboardingActions(grants);
  const [merchantId, setMerchantId] = useState<string | undefined>(merchant?.merchantId);
  const [facts, setFacts] = useState<StoreLiveFacts>({});
  const [location, setLocation] = useState<FoodMerchantLocationView | undefined>(undefined);
  const [reviewId, setReviewId] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ legalEntityRef: '', displayName: '', payoutRef: '', menuId: '', menuName: '', category: '', currency: '', address: '', latitude: '', longitude: '' });
  // D849 (P0-013): several first items (each its own name and price), and the 7-day opening hours.
  const [items, setItems] = useState<readonly { readonly name: string; readonly price: bigint | undefined }[]>([{ name: '', price: undefined }]);
  const [week, setWeek] = useState<WeekHours>(emptyWeek);
  const [hoursIssues, setHoursIssues] = useState<readonly StoreHoursIssue[]>([]);
  const [country, setCountry] = useState<string | undefined>(merchant?.scope.countryCode);
  const set = (k: keyof typeof form, v: string): void => setForm((f) => ({ ...f, [k]: v }));

  const explain = useCallback((e: ApiError): string => said(e) ?? t('pob.refused', { code: fail(e) }), [t, said, fail]);

  const refresh = useCallback(async (id: string | undefined): Promise<void> => {
    if (id === undefined) {
      setFacts({});
      return;
    }
    const got = await runtime.foodMerchant.getMerchant(id);
    if (!got.ok) {
      setError(explain(got.error));
      return;
    }
    const m = got.value.merchant;
    setCountry(m.scope.countryCode);
    const locs = await runtime.foodMerchant.listLocations(id);
    const loc = locs.ok ? locs.value.locations[0] : undefined;
    setLocation(loc);
    let profilePublished = false;
    let hoursSet = false;
    let menuId: string | null = null;
    let pickupPointSet = false;
    if (loc !== undefined) {
      const profile = await runtime.storeProfile.getProfile(loc.locationId);
      profilePublished = profile.ok && profile.value.record.profile.status === 'active';
      hoursSet = profile.ok && hasOpenHours(profile.value.record.hours);
      // Hours the server already holds come back into the grid when the grid can show them.
      const held = profile.ok && profile.value.record.hours !== undefined ? weekFromWindows(profile.value.record.hours.regular) : undefined;
      if (held !== undefined) setWeek(held);
      const setup = await runtime.foodMerchant.storeSetup(loc.locationId);
      if (setup.ok) {
        menuId = setup.value.menuId;
        pickupPointSet = setup.value.pickupPoint !== null;
      }
    }
    // D849 (P0-009): somebody who can run the store - an active owner or manager on its team.
    const team = await runtime.merchantConsole.listMemberships(id);
    const ownerSet = team.ok && hasStoreResponsible(team.value.memberships);
    let reviewStatus: string | undefined;
    if (reviewId !== undefined) {
      const review = await runtime.foodOnboardingReview.get(reviewId);
      if (review.ok) reviewStatus = review.value.submission.status;
    }
    setFacts({
      merchantStatus: m.status,
      ...(m.payoutAccountRef !== undefined ? { payoutAccountRef: m.payoutAccountRef } : {}),
      ...(loc !== undefined ? { locationId: loc.locationId } : {}),
      profilePublished,
      hoursSet,
      ownerSet,
      menuId,
      pickupPointSet,
      ...(reviewStatus !== undefined ? { reviewStatus } : {}),
    });
  }, [runtime, reviewId, explain]);

  useEffect(() => {
    if (!open) return;
    setMerchantId(merchant?.merchantId);
    setError(undefined);
    void refresh(merchant?.merchantId);
  }, [open, merchant, refresh]);

  const progress = storeLiveProgress(facts);
  const step: StoreLiveStep | undefined = progress.next;
  // The market is the header scope's (owner, 2026-10-12); once the unit exists, the unit's own market rules the profile.
  const market = markets.find((x) => x.id === location?.scope.marketId) ?? markets.find((x) => x.id === scopeMarket);
  const needsScopeMarket = (step === 'merchant' || step === 'unit') && market === undefined;

  /** Run one step's calls; refresh the facts from the server whatever happened. */
  const run = async (fn: () => Promise<{ ok: boolean; error?: ApiError; id?: string }>): Promise<void> => {
    setBusy(true);
    setError(undefined);
    const res = await fn();
    setBusy(false);
    if (!res.ok && res.error !== undefined) setError(explain(res.error));
    const id = res.id ?? merchantId;
    if (res.id !== undefined) setMerchantId(res.id);
    await refresh(id);
    onChanged();
  };

  const act = async (): Promise<void> => {
    const id = merchantId;
    switch (step) {
      case 'merchant':
        if (market === undefined) return;
        return run(async () => {
          const r = await runtime.foodMerchant.createMerchant({ legalEntityRef: form.legalEntityRef.trim(), countryConfigRef: `country-pack:${market.countryCode}`, scope: { platform: 'vello', countryCode: market.countryCode } });
          return r.ok ? { ok: true, id: r.value.merchant.merchantId } : { ok: false, error: r.error };
        });
      case 'unit':
        if (id === undefined || market === undefined) return;
        return run(async () => {
          const r = await runtime.foodMerchant.createLocation(id, { platform: 'vello', countryCode: market.countryCode, marketId: market.id });
          return r.ok ? { ok: true } : { ok: false, error: r.error };
        });
      case 'profile':
        if (id === undefined || location === undefined) return;
        return run(async () => {
          const existing = await runtime.storeProfile.getProfile(location.locationId);
          if (!existing.ok) {
            const pub = await runtime.storeProfile.publish({ merchantId: id, locationId: location.locationId, displayName: form.displayName.trim(), timezoneRef: market?.timezone ?? 'UTC' });
            if (!pub.ok) return { ok: false, error: pub.error };
          }
          const live = await runtime.storeProfile.transitionStatus(location.locationId, 'active');
          return live.ok ? { ok: true } : { ok: false, error: live.error };
        });
      case 'payout':
        if (id === undefined) return;
        return run(async () => {
          const r = await runtime.foodMerchant.setPayoutAccount(id, form.payoutRef.trim());
          return r.ok ? { ok: true } : { ok: false, error: r.error };
        });
      case 'hours': {
        if (location === undefined) return;
        const windows = weekToWindows(week);
        if (!windows.ok) {
          setHoursIssues(windows.issues);
          return;
        }
        setHoursIssues([]);
        return run(async () => {
          const r = await runtime.storeProfile.setHours(location.locationId, { version: `admin-${new Date().toISOString()}`, regular: windows.windows });
          return r.ok ? { ok: true } : { ok: false, error: r.error };
        });
      }
      case 'menu': {
        if (id === undefined || location === undefined) return;
        // A first menu is typed here when the operator may publish a catalog; otherwise the store's own menu is linked by its id.
        const typed = items.filter((it) => it.name.trim() !== '' || it.price !== undefined);
        const complete = typed.every((it) => it.name.trim() !== '' && it.price !== undefined);
        if (allowed.mayPublishMenu && form.menuId.trim() === '' && (typed.length === 0 || !complete)) {
          setError(t('pob.store.itemsIncomplete'));
          return;
        }
        return run(async () => {
          let menuId = form.menuId.trim();
          if (allowed.mayPublishMenu && menuId === '') {
            // A new store's first menu: one category and every item typed - effective now, claimed for this merchant (D394).
            menuId = newId();
            const categoryId = newId();
            const itemIds = typed.map(() => newId());
            const now = new Date().toISOString();
            const m = await runtime.foodCatalog.publishMenuVersion(menuId, { merchantId: id, version: 1, effectiveFrom: now, name: form.menuName.trim(), categoryIds: [categoryId] });
            if (!m.ok) return { ok: false, error: m.error };
            const c = await runtime.foodCatalog.publishCategoryVersion(categoryId, { merchantId: id, version: 1, effectiveFrom: now, menuId, name: form.category.trim(), itemIds });
            if (!c.ok) return { ok: false, error: c.error };
            for (const [index, it] of typed.entries()) {
              const published = await runtime.foodCatalog.publishItemVersion(itemIds[index] as string, { merchantId: id, version: 1, effectiveFrom: now, categoryId, name: it.name.trim(), basePrice: { amountMinor: (it.price as bigint).toString(), currency: form.currency } });
              if (!published.ok) return { ok: false, error: published.error };
            }
          }
          // D849 (P0-016): an operator who may not write a catalog (MERCHANT_OPS - only the merchant publishes, D391 #6) links the menu the store
          // already published by its code. With no code there is nothing to link: say so in words instead of sending an empty id for a generic refusal.
          if (menuId === '') return { ok: false, error: { code: 'BAD_REQUEST', status: 400, reasonCode: 'MENU_REQUIRED' } };
          const link = await runtime.foodMerchant.setStoreMenu(location.locationId, menuId);
          return link.ok ? { ok: true } : { ok: false, error: link.error };
        });
      }
      case 'pickup': {
        if (location === undefined) return;
        const latitude = parseCoordinate(form.latitude, 'latitude');
        const longitude = parseCoordinate(form.longitude, 'longitude');
        if (latitude === undefined || longitude === undefined) {
          setError(said({ code: 'BAD_REQUEST', reasonCode: 'PICKUP_POINT_INVALID' }) ?? v('api.error_code', 'BAD_REQUEST'));
          return;
        }
        return run(async () => {
          const r = await runtime.foodMerchant.setStorePickupPoint(location.locationId, { latitude, longitude, address: form.address.trim() });
          return r.ok ? { ok: true } : { ok: false, error: r.error };
        });
      }
      case 'submitted':
        if (id === undefined) return;
        return run(async () => {
          const r = await runtime.foodMerchant.submit(id);
          if (r.ok && r.value.reviewSubmissionId !== undefined) setReviewId(r.value.reviewSubmissionId);
          return r.ok ? { ok: true } : { ok: false, error: r.error };
        });
      case 'reviewed':
      case 'active':
        if (id === undefined) return;
        return run(async () => {
          const r = await runtime.foodMerchant.activate(id, { kybSatisfied: true, kybPolicyVersion: 'food-onboarding-review', kybReasonCode: 'ONBOARDING_REVIEW_APPROVED' });
          return r.ok ? { ok: true } : { ok: false, error: r.error };
        });
      default:
        return undefined;
    }
  };

  const dayName = (d: HoursDay): string => t(`sarea.day.${d}`);
  const editDay = (d: HoursDay, next: DayHours): void => {
    setHoursIssues([]);
    setWeek((w) => setDay(w, d, next));
  };
  const hoursIssueText = (issue: StoreHoursIssue): string => {
    if (issue === 'NO_OPEN_DAY') return t('pob.hours.issue.NO_OPEN_DAY');
    const [kind, day] = issue.split(':') as ['TIME_INVALID' | 'TIME_EQUAL', HoursDay];
    return t(`pob.hours.issue.${kind}`, { day: dayName(day) });
  };
  /** The 7-day grid: each day closed, open 24 hours, or open between two times (closing before opening runs past midnight). */
  const hoursGrid = (): ReactElement => (
    <Stack gap={3}>
      <Text as="p" role="caption" tone="secondary">{t('pob.hours.hint', { zone: market?.timezone ?? 'UTC' })}</Text>
      {HOURS_DAYS.map((d) => {
        const day = week[d];
        return (
          <Cluster key={d} gap={3} wrap align="end" role="group" aria-label={t('pob.hours.day', { day: dayName(d) })}>
            <Field label={dayName(d)} htmlFor={`sl-h-${d}`}>
              <Select id={`sl-h-${d}`} value={day.mode} onChange={(e) => editDay(d, e.target.value === 'window' ? { mode: 'window', open: '10:00', close: '22:00' } : { mode: e.target.value as 'closed' | 'all_day' })}>
                <option value="closed">{t('pob.hours.closed')}</option>
                <option value="all_day">{t('pob.hours.allDay')}</option>
                <option value="window">{t('pob.hours.window')}</option>
              </Select>
            </Field>
            {day.mode === 'window' && (
              <>
                <Field label={t('pob.hours.open', { day: dayName(d) })} htmlFor={`sl-h-${d}-open`}><TextInput id={`sl-h-${d}-open`} type="time" className="tnum" value={day.open} onChange={(e) => editDay(d, { ...day, open: e.target.value })} /></Field>
                <Field label={t('pob.hours.close', { day: dayName(d) })} htmlFor={`sl-h-${d}-close`}><TextInput id={`sl-h-${d}-close`} type="time" className="tnum" value={day.close} onChange={(e) => editDay(d, { ...day, close: e.target.value })} /></Field>
              </>
            )}
            {day.mode === 'window' && day.open !== '' && day.close !== '' && day.close < day.open && <Text role="caption" tone="secondary">{t('pob.hours.overnight')}</Text>}
          </Cluster>
        );
      })}
      <div><Button variant="ghost" onClick={() => { setHoursIssues([]); setWeek(sameEveryDay(week.mon)); }}>{t('pob.hours.copyMonday')}</Button></div>
      {hoursIssues.map((issue) => <Notice key={issue} tone="danger">{hoursIssueText(issue)}</Notice>)}
    </Stack>
  );

  const stepForm = (): ReactElement | null => {
    switch (step) {
      case 'merchant':
        return (
          <>
            <Field label={t('pob.store.legalEntityRef')} htmlFor="sl-legal"><TextInput id="sl-legal" value={form.legalEntityRef} onChange={(e) => set('legalEntityRef', e.target.value)} /></Field>
            {market === undefined && <ScopeMarketNotice choices={marketChoices} />}
          </>
        );
      case 'unit':
        return market === undefined ? <ScopeMarketNotice choices={marketChoices} /> : <Text role="caption" tone="secondary">{`${market.name} (${market.id})`}</Text>;
      case 'profile':
        return <Field label={t('pob.store.displayName')} htmlFor="sl-name"><TextInput id="sl-name" value={form.displayName} onChange={(e) => set('displayName', e.target.value)} /></Field>;
      case 'hours':
        return hoursGrid();
      case 'owner':
        return mayManageStoreTeam(grants) && merchantId !== undefined ? (
          <StoreResponsibleInvite merchantId={merchantId} countryCode={country} primary onDone={() => void refresh(merchantId).then(onChanged)} />
        ) : (
          <Text as="p" role="caption" tone="secondary">{t('w6.noPermission.action')}</Text>
        );
      case 'payout':
        return <Field label={t('pob.store.payoutRef')} htmlFor="sl-payout"><TextInput id="sl-payout" value={form.payoutRef} onChange={(e) => set('payoutRef', e.target.value)} /></Field>;
      case 'menu':
        return allowed.mayPublishMenu ? (
          <>
            <Field label={t('pob.store.menuName')} htmlFor="sl-menu"><TextInput id="sl-menu" value={form.menuName} onChange={(e) => set('menuName', e.target.value)} /></Field>
            <Field label={t('pob.store.category')} htmlFor="sl-cat"><TextInput id="sl-cat" value={form.category} onChange={(e) => set('category', e.target.value)} /></Field>
            <Text as="p" role="caption" tone="secondary">{t('pob.store.itemsHint')}</Text>
            <Field label={t('pob.field.currency')} htmlFor="sl-currency"><TextInput id="sl-currency" maxLength={3} value={form.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} /></Field>
            {items.map((it, index) => (
              <Stack key={index} gap={2}>
                <Field label={t('pob.store.itemN', { n: String(index + 1) })} htmlFor={`sl-item-${index}`}><TextInput id={`sl-item-${index}`} value={it.name} onChange={(e) => setItems((all) => all.map((x, i) => (i === index ? { ...x, name: e.target.value } : x)))} /></Field>
                {isIso4217Currency(form.currency) && <MoneyInput id={`sl-price-${index}`} label={t('pob.store.price')} currency={form.currency} locale={lang} valueMinor={it.price} onChangeMinor={(minor) => setItems((all) => all.map((x, i) => (i === index ? { ...x, price: minor } : x)))} issueText={(issue) => v('format.money_input_issue', issue)} />}
                {items.length > 1 && <div><Button variant="ghost" onClick={() => setItems((all) => all.filter((_, i) => i !== index))}>{t('pob.store.removeItem', { n: String(index + 1) })}</Button></div>}
              </Stack>
            ))}
            <div><Button variant="secondary" onClick={() => setItems((all) => [...all, { name: '', price: undefined }])}>{t('pob.store.addItem')}</Button></div>
          </>
        ) : (
          <>
            <Notice tone="neutral">{t('pob.store.menuByMerchant')}</Notice>
            <Field label={t('pob.store.menuId')} htmlFor="sl-menu-id"><TextInput id="sl-menu-id" value={form.menuId} onChange={(e) => set('menuId', e.target.value)} /></Field>
          </>
        );
      case 'pickup':
        return (
          <>
            <Field label={t('pob.field.door')} htmlFor="sl-addr"><TextInput id="sl-addr" value={form.address} onChange={(e) => set('address', e.target.value)} /></Field>
            <Field label={t('pob.field.latitude')} htmlFor="sl-lat"><TextInput id="sl-lat" inputMode="decimal" className="tnum" value={form.latitude} onChange={(e) => set('latitude', e.target.value)} /></Field>
            <Field label={t('pob.field.longitude')} htmlFor="sl-lng"><TextInput id="sl-lng" inputMode="decimal" className="tnum" value={form.longitude} onChange={(e) => set('longitude', e.target.value)} /></Field>
          </>
        );
      case 'reviewed':
        return <Notice tone="neutral">{t('pob.store.waitingReview')}</Notice>;
      default:
        return null;
    }
  };

  const label = step === 'submitted' ? t('pob.store.submit') : step === 'reviewed' || step === 'active' ? t('pob.store.activate') : t('pob.store.next');
  const mayAct = step === 'reviewed' || step === 'active' ? allowed.mayActivate : allowed.mayOnboard;

  return (
    <Drawer open={open} onClose={onClose} labelledBy="sl-title">
      <DrawerHeader titleId="sl-title" title={t('pob.store.title')} subtitle={t('pob.store.subtitle')} />
      <DrawerBody>
        <DrawerStack>
          {error !== undefined && <Notice tone="danger">{error}</Notice>}
          <ol aria-label={t('pob.store.title')} className="vello-admin-a-steps">
            {progress.steps.map((s) => (
              <li key={s.step} aria-current={s.step === step ? 'step' : undefined}>
                {t(`pob.store.step.${s.step}`)} <Badge tone={s.done ? 'success' : 'neutral'}>{t(s.done ? 'pob.store.done' : 'pob.store.todo')}</Badge>
              </li>
            ))}
          </ol>
          {step === undefined ? (
            <Notice tone="success">{t('pob.store.live')}</Notice>
          ) : (
            <>
              {stepForm()}
              {step === 'owner' ? null : mayAct ? (
                <div>
                  <Button variant="primary" disabled={busy || needsScopeMarket} onClick={() => void act()}>{label}</Button>
                </div>
              ) : (
                <Text as="p" role="caption" tone="secondary">{t('w6.noPermission.action')}</Text>
              )}
            </>
          )}
        </DrawerStack>
      </DrawerBody>
    </Drawer>
  );
}
